import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Clock } from 'lucide-react';
import {
  AnnouncementBand,
  Button,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusBadge,
  SubBar,
} from '@/design/components';
import { cn } from '@/lib/cn';
import { useMe } from '@/auth/useMe';
import { formatDate, formatDateTime, timeLeftLabel, toDateKey, weekdayName } from '@/lib/dates';
import { useNow } from '@/lib/useNow';
import { errorMessage } from '@/lib/errors';
import {
  findClosure,
  useClosures,
  useConstraintMutations,
  useConstraintTypes,
  useMyConstraints,
  useMySubmission,
  useOpenPeriod,
  usePeriod,
  useShiftTypes,
} from './api';

/**
 * הגשת אילוצים (PRD 5.4.2, מסמך התכנון 9.3).
 * לחיצה על תא מחליפה מצב לפי סדר constraint_types, ואחרי האחרון
 * חוזרת לפנוי. כל לחיצה נשמרת מיד.
 */
export function ConstraintsPage() {
  const { periodId: fromUrl } = useParams();
  const open = useOpenPeriod();
  const periodId = fromUrl ?? open.data?.id ?? '';

  if (!fromUrl && open.isPending) return <Skeleton className="m-4 h-40" />;
  if (!periodId) {
    return (
      <div className="grid gap-4">
        <SubBar title="הגשת אילוצים" />
        <EmptyState
          title="אין תקופה פתוחה להגשה"
          description="כשמנהל/ת הסניף יפתח/תפתח תקופה, תגיע התראה."
        />
      </div>
    );
  }
  return <Grid periodId={periodId} />;
}

function Grid({ periodId }: { periodId: string }) {
  const { me } = useMe();
  const period = usePeriod(periodId);
  const shiftTypes = useShiftTypes(period.data?.branch_id);
  const closures = useClosures(
    period.data?.branch_id,
    period.data?.start_date ?? '',
    period.data?.end_date ?? '',
  );
  const types = useConstraintTypes();
  const constraints = useMyConstraints(periodId);
  const submission = useMySubmission(periodId);
  const { setCell, submit } = useConstraintMutations(periodId, me?.employeeId);
  const now = useNow(60_000);

  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const days = useMemo(() => {
    if (!period.data) return [];
    const list: string[] = [];
    const end = new Date(period.data.end_date);
    for (let d = new Date(period.data.start_date); d <= end; d.setDate(d.getDate() + 1)) {
      list.push(toDateKey(new Date(d)));
    }
    return list;
  }, [period.data]);

  const byCell = useMemo(() => {
    const map = new Map<string, { id: string; typeId: string }>();
    for (const c of constraints.data ?? []) {
      map.set(`${c.date}|${c.shift_type_id ?? ''}`, {
        id: c.id,
        typeId: c.constraint_type_id,
      });
    }
    return map;
  }, [constraints.data]);

  if (period.isError) return <ErrorState onRetry={() => void period.refetch()} />;
  if (!period.data || !types.data || !shiftTypes.data) {
    return <Skeleton className="m-4 h-64" />;
  }

  const isPast = new Date(period.data.constraints_deadline).getTime() < now;
  const locked = isPast || period.data.status !== 'collecting';
  const submitted = submission.data?.submitted_at ?? null;
  const ordered = types.data;

  /** המצב הבא בסבב: פנוי, ואז כל הסוגים לפי הסדר, וחזרה לפנוי. */
  function nextType(currentId: string | undefined): string | null {
    if (!currentId) return ordered[0]?.id ?? null;
    const index = ordered.findIndex((type) => type.id === currentId);
    return index === -1 || index === ordered.length - 1 ? null : ordered[index + 1].id;
  }

  const marked = constraints.data?.length ?? 0;

  return (
    <div className="grid content-start">
      <SubBar
        title="הגשת אילוצים"
        subtitle={`${formatDate(period.data.start_date)} עד ${formatDate(period.data.end_date)}`}
      />

      {!locked && (
        <AnnouncementBand icon={Clock}>
          {timeLeftLabel(period.data.constraints_deadline)} להגשה
        </AnnouncementBand>
      )}

      <div className="grid gap-4 p-4 pb-28">
        {locked && (
          <StatusBadge tone="warn">
            {isPast ? 'המועד להגשה עבר' : 'התקופה כבר לא פתוחה להגשה'}
          </StatusBadge>
        )}

        <div className="flex flex-wrap gap-2">
          {ordered.map((type) => (
            <span
              key={type.id}
              className="flex items-center gap-1 rounded-pill border border-muted px-3 py-1 text-label"
            >
              <span
                aria-hidden="true"
                className="size-3 rounded-pill border border-line"
                style={{ backgroundColor: type.color ?? 'transparent' }}
              />
              {type.name}
            </span>
          ))}
        </div>

        {error && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {error}
          </p>
        )}

        {shiftTypes.data.length === 0 ? (
          <EmptyState
            title="אין סוגי משמרות לסניף"
            description="מנהל/ת צריך/ה להגדיר אותם בהגדרות."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-body-sm">
              <caption className="sr-only">טבלת אילוצים: שורה לכל יום, עמודה לכל סוג משמרת</caption>
              <thead>
                <tr>
                  <th scope="col" className="p-2 text-start">
                    יום
                  </th>
                  {shiftTypes.data.map((shift) => (
                    <th key={shift.id} scope="col" className="p-2 text-center">
                      <span className="grid">
                        <span>{shift.name}</span>
                        <span className="text-label font-normal text-muted tabular">
                          {shift.start_time.slice(0, 5)}
                        </span>
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {days.map((date) => {
                  const dayClosure = findClosure(closures.data, date, null);

                  return (
                    <tr key={date} className="border-t border-line">
                      <th scope="row" className="p-2 text-start font-normal">
                        <span className="grid">
                          <span>{weekdayName(date)}</span>
                          <span className="text-label text-muted tabular">
                            {date.slice(8, 10)}.{date.slice(5, 7)}
                          </span>
                        </span>
                      </th>

                      {/*
                      יום סגור לא מקבל תאים: אין טעם שהעובד יסמן אילוץ
                      ליום שלא עובדים בו. הסיבה מוצגת, כדי שלא ישאל.
                    */}
                      {dayClosure?.is_closed ? (
                        <td colSpan={shiftTypes.data.length} className="p-1">
                          <p className="m-0 grid h-13 place-content-center rounded-sm border border-dashed border-muted/50 bg-charcoal/5 text-center text-label text-muted">
                            סגור — {dayClosure.label}
                          </p>
                        </td>
                      ) : (
                        shiftTypes.data.map((shift) => {
                          const cell = byCell.get(`${date}|${shift.id}`);
                          const type = ordered.find((t) => t.id === cell?.typeId);

                          return (
                            <td key={shift.id} className="p-1">
                              <button
                                type="button"
                                disabled={locked || setCell.isPending}
                                aria-label={`${weekdayName(date)} ${shift.name}: ${type?.name ?? 'פנוי'}`}
                                onClick={() => {
                                  setError(null);
                                  setCell.mutate(
                                    {
                                      date,
                                      shiftTypeId: shift.id,
                                      existingId: cell?.id ?? null,
                                      constraintTypeId: nextType(cell?.typeId),
                                    },
                                    { onError: (caught) => setError(errorMessage(caught)) },
                                  );
                                }}
                                className={cn(
                                  'grid h-13 w-full place-items-center rounded-sm border px-1 text-label',
                                  type ? 'border-transparent text-ink' : 'border-line text-muted',
                                  locked && 'cursor-not-allowed opacity-60',
                                )}
                                style={type?.color ? { backgroundColor: type.color } : undefined}
                              >
                                {type?.name ?? '־'}
                              </button>
                            </td>
                          );
                        })
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="grid gap-1">
          <label htmlFor="general-note" className="text-label font-medium">
            הערה למנהל/ת הסניף
          </label>
          <textarea
            id="general-note"
            rows={2}
            disabled={locked}
            value={note || (submission.data?.general_note ?? '')}
            onChange={(event) => setNote(event.target.value)}
            className="rounded-sm border border-muted bg-surface p-2 text-body"
          />
        </div>
      </div>

      <div className="sticky bottom-0 grid gap-2 border-t border-line bg-surface p-4">
        <p className="m-0 text-body-sm text-muted">
          סימנת {marked} משמרות.
          {submitted && ` הגשת ב ${formatDateTime(submitted)}.`}
        </p>
        <Button
          variant="primary"
          block
          disabled={locked || submit.isPending}
          onClick={() => {
            setError(null);
            submit.mutate(note, { onError: (caught) => setError(errorMessage(caught)) });
          }}
        >
          {submitted ? 'עדכון ההגשה' : 'שליחת אילוצים'}
        </Button>
      </div>
    </div>
  );
}
